import { ReloadOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import type { KzDataCheck } from "@wms/domain";
import { Alert, Button, Card, Col, Empty, Row, Space, Statistic, Table, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { formatDateTime } from "../api/format";
import { useAuthStore } from "../auth/store";
import { LoadProblem } from "../components/LoadProblem";
import { PageHeader } from "../components/PageHeader";

const PROBLEM_WORDS: Record<string, string> = { missing: "не заполнен", format: "неверный формат", checksum: "контрольная цифра" };

/**
 * KZ 30.10.2026 (ТЗ реквизитов §15.1): «Проверка данных под Казахстан» — what the localization leaves to people. Read
 * only: nothing here changes a card; every row opens the card where an authorized employee fixes it. Identifiers are
 * masked to the last four digits.
 */
export function KzDataCheckPage() {
  const can = useAuthStore((s) => s.can);
  const allowed = can("core.data_check.read");
  const check = useQuery({ queryKey: ["kz-data-check"], queryFn: () => api.get<KzDataCheck>("/admin/kz-data-check"), enabled: allowed });
  if (!allowed) {
    return (
      <div>
        <PageHeader title="Проверка данных под Казахстан" />
        <Alert type="info" showIcon message="Нет права «Проверка данных под Казахстан: просмотр»" description="Право назначает администратор в карточке роли." />
      </div>
    );
  }
  if (check.isError) return <LoadProblem error={check.error} onRetry={() => check.refetch()} />;
  const d = check.data;
  const link = (path: string | null, text: string) => (path ? <Link to={path}>{text}</Link> : text);
  return (
    <div data-testid="kz-data-check">
      <PageHeader
        title="Проверка данных под Казахстан"
        subtitle="Только чтение: формы контрагентов, прежние реквизиты, БИН/ИИН, реквизиты организаций, пояса складов"
        extra={
          <Space>
            <SafetyCertificateOutlined style={{ fontSize: 22, color: "var(--wms-muted)" }} />
            <Button icon={<ReloadOutlined />} loading={check.isFetching} onClick={() => check.refetch()}>
              Обновить
            </Button>
          </Space>
        }
      />
      {d ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            Сформировано {formatDateTime(d.generated_at)}. Номера показаны последними четырьмя цифрами; списки — до {d.limit} строк, счётчики полные.
          </Typography.Text>

          <Card title={`Контрагенты по форме (всего ${d.partners.total})`} size="small">
            <Row gutter={[12, 12]}>
              {d.partners.forms.map((f) => (
                <Col key={String(f.kind)} xs={12} md={6} lg={4}>
                  <Statistic title={f.legacy ? <span>{f.label} <Tag>прежняя</Tag></span> : f.label} value={f.count} />
                </Col>
              ))}
            </Row>
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Прежние формы («ТОО / ООО», «Физическое лицо» …) не переводятся в ТОО автоматически: форму меняет сотрудник в карточке.
            </Typography.Paragraph>
          </Card>

          <Card title={`Прежние ИНН / КПП: ИНН у ${d.partners.legacy_fields.inn}, КПП у ${d.partners.legacy_fields.kpp}`} size="small">
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 10 }}
              dataSource={d.partners.legacy_fields.items}
              locale={{ emptyText: <Empty description="Нет карточек с ИНН или КПП" /> }}
              columns={[
                { title: "Карточка", render: (_, r) => link(r.path, r.name) },
                { title: "ИНН", dataIndex: "inn", render: (v: string | null) => v ?? "—" },
                { title: "КПП", dataIndex: "kpp", render: (v: string | null) => v ?? "—" },
                { title: "БИН/ИИН", dataIndex: "tax_id", render: (v: string | null) => v ?? "не заполнен" },
              ]}
            />
          </Card>

          <Card
            title={`БИН / ИИН: не заполнен — ${d.partners.tax_id_problems.missing}, неверный формат — ${d.partners.tax_id_problems.format}, контрольная цифра — ${d.partners.tax_id_problems.checksum}`}
            size="small"
          >
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 10 }}
              dataSource={d.partners.tax_id_problems.items}
              columns={[
                { title: "Карточка", render: (_, r) => link(r.path, r.name) },
                { title: "Форма", dataIndex: "form", render: (v: string | null) => v ?? "не указана" },
                { title: "Номер", dataIndex: "tax_id", render: (v: string | null) => v ?? "—" },
                { title: "Что не так", render: (_, r) => <span><Tag color={r.problem === "missing" ? "default" : "orange"}>{PROBLEM_WORDS[r.problem] ?? r.problem}</Tag> {r.text}</span> },
                { title: "Роли", dataIndex: "roles", render: (v: string[]) => v.join(", ") || "—" },
              ]}
            />
          </Card>

          <Card title={`Повторы идентификатора внутри справочника: ${d.partners.duplicates.length}`} size="small">
            <Table
              size="small"
              rowKey={(r) => `${r.catalogue}-${r.tax_id}-${r.cards.map((c) => c.id).join()}`}
              pagination={{ pageSize: 10 }}
              dataSource={d.partners.duplicates}
              locale={{ emptyText: <Empty description="Повторов нет" /> }}
              columns={[
                { title: "Справочник", dataIndex: "catalogue_label", width: 140 },
                { title: "БИН/ИИН", dataIndex: "tax_id", width: 160 },
                { title: "Карточки", render: (_, r) => <Space wrap>{r.cards.map((c) => <Link key={c.id} to={c.path}>{c.name}</Link>)}</Space> },
              ]}
            />
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Совпадение номера не объединяет карточки и не переносит договоры: решение принимает сотрудник.
            </Typography.Paragraph>
          </Card>

          <Card title={`Мои организации с неполными реквизитами: ${d.companies.length}`} size="small">
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.companies}
              locale={{ emptyText: <Empty description="Реквизиты всех организаций заполнены" /> }}
              columns={[
                { title: "Организация", render: (_, r) => link(r.path, r.name) },
                { title: "Не заполнено", dataIndex: "missing", render: (v: string[]) => v.join(", ") || "—" },
                { title: "Проверить", dataIndex: "invalid", render: (v: string[]) => (v.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>{v.map((t) => <li key={t}>{t}</li>)}</ul> : "—") },
              ]}
            />
          </Card>

          <Card title={`Склады не в поясе ${d.default_timezone_label}: ${d.warehouses.length}`} size="small">
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.warehouses}
              locale={{ emptyText: <Empty description="Все склады в поясе Казахстана" /> }}
              columns={[
                { title: "Склад", render: (_, r) => link(r.path, `${r.code} — ${r.name}`) },
                { title: "Пояс", dataIndex: "timezone_label" },
                { title: "Поступлений", dataIndex: "receipts", width: 120 },
                { title: "Заказов", dataIndex: "orders", width: 100 },
                { title: "Строк остатков", dataIndex: "stock_rows", width: 130 },
              ]}
            />
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Смена пояса меняет день склада для новых операций; время событий хранится в UTC, проведённые документы не пересчитываются.
            </Typography.Paragraph>
          </Card>

          <Card title="Коды маркировки и серийные номера по виду" size="small">
            <Row gutter={[12, 12]}>
              {d.serial_kinds.map((k) => (
                <Col key={k.kind} xs={12} md={6}>
                  <Statistic title={k.label} value={k.count} />
                </Col>
              ))}
            </Row>
          </Card>

          <Card title="Документы, где сторона — карточка прежней формы" size="small">
            <Table
              size="small"
              rowKey={(r) => `${r.table}-${r.field}`}
              pagination={false}
              dataSource={d.documents_of_legacy_partners}
              locale={{ emptyText: <Empty description="Нет документов со сторонами прежних форм" /> }}
              columns={[
                { title: "Документы", dataIndex: "label" },
                { title: "Поле", dataIndex: "field" },
                { title: "Сколько", dataIndex: "count", width: 100 },
                { title: "Например", render: (_, r) => <Space wrap>{r.examples.map((e) => <span key={e.id}>{link(e.path, e.number)}</span>)}</Space> },
              ]}
            />
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Документы остаются открываемыми и не переписываются: исправление карточки не меняет проведённые документы.
            </Typography.Paragraph>
          </Card>
        </Space>
      ) : (
        <Card loading />
      )}
    </div>
  );
}
